import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Star,
  ArrowClockwise,
  CheckCircle,
  Sparkle,
  TrendUp,
  Warning,
  BookOpen,
  DownloadSimple,
  ChartBar,
} from "@phosphor-icons/react";
import { REPORT } from "@/constants/testIds";
import { makeT } from "@/lib/i18n";
import { reportT, levelHeadline, levelCopy } from "@/lib/reportI18n";
import { useAuth } from "@/context/AuthContext";

function scoreColor(v) {
  if (v >= 75) return "var(--score-good)";
  if (v >= 50) return "var(--score-average)";
  return "var(--score-bad)";
}

function ScoreBar({ label, value }) {
  const isNA = value === null || value === undefined;
  const pct = isNA ? 0 : Number(value);
  return (
    <div data-testid={isNA ? "score-bar-na" : undefined}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[color:var(--text-secondary)] uppercase tracking-widest text-[10px]">{label}</span>
        <span className="mono font-medium">{isNA ? "—" : value}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-[color:var(--border-stone)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: isNA ? "0%" : `${pct}%`,
            background: isNA ? "transparent" : scoreColor(pct),
          }}
        />
      </div>
    </div>
  );
}

export default function Report() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [repracticeOpen, setRepracticeOpen] = useState(false);
  const [repracticeLoading, setRepracticeLoading] = useState(false);
  const [betaStatusReady, setBetaStatusReady] = useState(false);
  const [betaAlreadySubmitted, setBetaAlreadySubmitted] = useState(false);
  const { user } = useAuth();
  const bootLang = user?.preferred_language || "en";
  const bootTr = reportT(bootLang);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/sessions/${sessionId}/report`),
      api.get(`/sessions/${sessionId}`).catch(() => ({ data: {} })),
    ])
      .then(([reportResponse, sessionResponse]) => {
        if (cancelled) return;
        const report = reportResponse.data || {};
        const session = sessionResponse.data || {};
        setData({
          ...report,
          questions: report.questions || session.questions || [],
        });
      })
      .catch(() => navigate("/"));
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  useEffect(() => {
    if (!sessionId || !user?.beta_access_approved) {
      setBetaStatusReady(false);
      setBetaAlreadySubmitted(false);
      return;
    }
    let cancelled = false;
    api
      .get(`/beta-feedback/status/${sessionId}`)
      .then((r) => {
        if (cancelled) return;
        setBetaAlreadySubmitted(Boolean(r.data?.submitted));
        setBetaStatusReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setBetaStatusReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, user?.beta_access_approved]);

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]"
        data-testid="report-loading"
      >
        {bootTr("rp.loading")}
      </div>
    );
  }

  const summary = data.summary || {};
  const answers = data.answers || [];
  const questions = data.questions || [];
  const lang =
    data.interface_language || user?.preferred_language || data.language || "en";
  const t = makeT(lang);
  const tr = reportT(lang);
  const usePt = String(lang).toLowerCase().startsWith("pt");

  const isRepractice = !!data.parent_session_id;
  const isOriginalCompleted = !isRepractice && !!data.summary;
  const canRepractice =
    data.repractice_enabled === true &&
    isOriginalCompleted &&
    !data.repractice_used;
  const showRepracticeUsedNote =
    data.repractice_enabled === true &&
    isOriginalCompleted &&
    data.repractice_used === true;

  async function startRepractice() {
    if (repracticeLoading) return;
    setRepracticeLoading(true);
    try {
      const res = await api.post(`/sessions/${sessionId}/repractice`);
      const newId = res.data && res.data.session_id;
      if (!newId) throw new Error("no_session_id_in_response");
      navigate(`/interview/${newId}`);
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const status = err?.response?.status;
      if (code === "repractice_already_used") {
        toast.error(t("rp.repracticeErrAlreadyUsed"));
        setData((d) => (d ? { ...d, repractice_used: true } : d));
      } else if (status === 402 || code === "insufficient_credits") {
        toast.error(t("rp.repracticeErrNoCredits"));
      } else {
        toast.error(t("rp.repracticeErrGeneric"));
      }
    } finally {
      setRepracticeLoading(false);
      setRepracticeOpen(false);
    }
  }

  return (
    <div className="min-h-screen" data-testid={REPORT.container}>
      <header className="border-b border-[color:var(--border-stone)] bg-white/70 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-8 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="My English Coach Interview Simulator logo"
              className="h-[63px] w-[63px] object-contain"
            />
            <div className="leading-tight">
              <div className="flex items-center gap-2">
                <span className="font-semibold tracking-tight">My English Coach Interview Simulator</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.beta_access_approved
              && data?.status === "completed"
              && betaStatusReady
              && !betaAlreadySubmitted && (
              <Button
                data-testid={REPORT.betaFeedbackBtn}
                variant="outline"
                onClick={() => navigate(`/beta-feedback/${sessionId}`)}
                className="rounded-full border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)] hover:text-white"
              >
                {t("rp.betaFeedback")}
              </Button>
            )}
            <Button
              data-testid={REPORT.restartBtn}
              onClick={() => navigate("/")}
              className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white"
            >
              <ArrowClockwise className="mr-2" size={16} /> {t("rp.newInterview")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 py-6 sm:py-12">
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="order-2 lg:order-1 lg:col-span-5 rounded-3xl bg-[color:var(--brand)] text-white p-6 sm:p-10 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/70">
                {t("rp.overall")}
              </div>
              <div
                className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1] break-words"
                data-testid={REPORT.overallScore}
              >
                {levelHeadline(summary.overall ?? 0, tr)}
              </div>
              <p className="mt-5 text-white/85 leading-relaxed max-w-sm">
                {levelCopy(summary.overall ?? 0, tr)}
              </p>
              <p className="mt-4 text-white/70 leading-relaxed max-w-sm text-sm">
                {t("rp.answeredOf", summary.answered ?? 0, summary.total ?? 0)}
              </p>
            </div>
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">
                {t("rp.interviewPerformance")}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  [t("rp.relevance"), summary.relevance],
                  [t("rp.supportingEvidence"), summary.supporting_evidence],
                  [t("rp.organization"), summary.organization],
                  [t("rp.professionalCompetency"), summary.professional_competency],
                  [t("rp.evidenceOfResults"), summary.evidence_of_results],
                  [t("rp.professionalPresence"), summary.professional_presence],
                ].map(([label, v]) => (
                  <div key={label}>
                    <div className="text-[10px] uppercase tracking-widest text-white/70">
                      {label}
                    </div>
                    <div className="mono text-2xl">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-[10px] uppercase tracking-widest text-white/60 mb-3">
                {t("rp.englishLanguage")}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {[
                  [t("rp.grammar"), summary.grammar, summary.grammar_assessed],
                  [t("rp.vocabulary"), summary.vocabulary, summary.vocabulary_assessed],
                  [t("rp.clarity"), summary.clarity, summary.clarity_assessed],
                  [t("rp.protone"), summary.professional_tone, summary.professional_tone_assessed],
                  [t("rp.fluency"), summary.fluency, summary.fluency_assessed],
                ].map(([label, v, assessed]) => {
                  const total = summary.answered ?? 0;
                  const showCoverage =
                    assessed != null && total > 0 && assessed > 0 && assessed < total;
                  return (
                    <div key={label}>
                      <div className="text-[10px] uppercase tracking-widest text-white/70">
                        {label}
                      </div>
                      <div className="mono text-2xl">{v ?? "—"}</div>
                      {showCoverage && (
                        <div
                          className="text-[9px] uppercase tracking-wider text-white/50 mt-0.5"
                          data-testid={`english-coverage-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {t("rp.englishAssessedOf", assessed, total)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-7 rounded-3xl border border-[color:var(--border-stone)] bg-white p-10">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
              {t("rp.summary")}
            </div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
              {t("rp.summaryH")}
            </h1>
            <p className="mt-4 text-[color:var(--text-secondary)] leading-relaxed">
              {t("rp.summaryP")}
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-[color:var(--bg-surface-hover)] p-5">
                <div className="flex items-center gap-2 text-[color:var(--brand)] font-semibold">
                  <Sparkle weight="duotone" /> {t("rp.bestArea")}
                </div>
                <div className="mt-2 text-sm text-[color:var(--text-secondary)]">
                  {bestArea(summary, tr)}
                </div>
              </div>
              <div className="rounded-2xl bg-[color:var(--bg-surface-hover)] p-5">
                <div className="flex items-center gap-2 text-[color:var(--score-bad)] font-semibold">
                  <TrendUp weight="duotone" /> {t("rp.focusNext")}
                </div>
                <div className="mt-2 text-sm text-[color:var(--text-secondary)]">
                  {worstArea(summary, tr)}
                </div>
              </div>
            </div>

            <DownloadsCard sessionId={sessionId} t={t} tr={tr} />
          </div>
        </section>

        {canRepractice && (
          <section
            data-testid="repractice-cta-section"
            className="mt-10 rounded-3xl border border-[color:var(--border-stone)] bg-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
          >
            <div>
              <h3
                className="text-xl sm:text-2xl font-semibold tracking-tight"
                data-testid="repractice-cta-heading"
              >
                {t("rp.repracticeH")}
              </h3>
              <p className="mt-2 text-[color:var(--text-secondary)] leading-relaxed max-w-xl">
                {t("rp.repracticeP")}
              </p>
              <p
                className="mt-3 text-sm font-medium text-[color:var(--brand)]"
                data-testid="repractice-cta-meta"
              >
                {t("rp.repracticeMeta")}
              </p>
            </div>
            <Button
              data-testid="repractice-cta-button"
              onClick={() => setRepracticeOpen(true)}
              disabled={repracticeLoading}
              className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-6 text-base font-medium shrink-0"
            >
              <ArrowClockwise className="mr-2" size={18} />
              {t("rp.repracticeCta")}
            </Button>
          </section>
        )}

        {showRepracticeUsedNote && (
          <section
            data-testid="repractice-used-note"
            className="mt-10 rounded-3xl border border-dashed border-[color:var(--border-stone)] bg-[color:var(--surface-muted)] p-6 text-sm text-[color:var(--text-secondary)] text-center"
          >
            {t("rp.repracticeUsed")}
          </section>
        )}

        {repracticeOpen && (
          <div
            data-testid="repractice-confirm-overlay"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
            onClick={() => !repracticeLoading && setRepracticeOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl"
              data-testid="repractice-confirm-dialog"
            >
              <h4
                className="text-xl font-semibold tracking-tight"
                data-testid="repractice-confirm-title"
              >
                {t("rp.repracticeConfirmTitle")}
              </h4>
              <p
                className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed"
                data-testid="repractice-confirm-intro"
              >
                {t("rp.repracticeConfirmIntro")}
              </p>
              <p className="mt-5 text-sm font-medium">
                {t("rp.repracticeConfirmBeforeH")}
              </p>
              <ul className="mt-2 space-y-3 text-sm text-[color:var(--text-secondary)] leading-relaxed list-disc pl-5">
                <li data-testid="repractice-confirm-bullet-1">
                  {t("rp.repracticeConfirmBullet1")}
                </li>
                <li data-testid="repractice-confirm-bullet-2">
                  {t("rp.repracticeConfirmBullet2")}
                </li>
              </ul>
              <div className="mt-7 flex items-center justify-end">
                <Button
                  data-testid="repractice-confirm-yes"
                  onClick={startRepractice}
                  disabled={repracticeLoading}
                  className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-6 text-base font-medium"
                >
                  {repracticeLoading
                    ? t("rp.repracticeStarting")
                    : t("rp.repracticeConfirmYes")}
                </Button>
              </div>
            </div>
          </div>
        )}

        <section className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              {t("rp.perQuestion")}
            </h2>
            <div className="text-sm text-[color:var(--text-secondary)]">
              {t("rp.answeredCount", answers.length, questions.length)}
            </div>
          </div>

          <div className="space-y-6">
            {questions.map((q, i) => {
              const a = answers.find((x) => x.question_index === i);
              const pt = usePt ? a?.evaluation?.pt_br : null;
              const strengths = (pt?.strengths ?? a?.evaluation?.strengths) || [];
              const improvements = (pt?.improvements ?? a?.evaluation?.improvements) || [];
              return (
                <div
                  key={i}
                  data-testid={REPORT.questionCard(i)}
                  className="rounded-3xl border border-[color:var(--border-stone)] bg-white p-8"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand)]">
                        {t("iv.questionN", i + 1).toUpperCase()} · {q.focus}
                      </div>
                      <div className="mt-2 text-lg font-semibold tracking-tight leading-snug">
                        {q.text}
                      </div>
                    </div>
                    {a?.evaluation ? (
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">
                          {t("rp.overallShort")}
                        </div>
                        <div
                          className="mono text-3xl font-semibold"
                          style={{ color: scoreColor(a.evaluation.overall) }}
                        >
                          {a.evaluation.overall}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-[color:var(--text-secondary)] shrink-0">
                        {t("rp.notAnswered")}
                      </div>
                    )}
                  </div>

                  {a?.evaluation && (
                    <>
                      <div className="mt-6">
                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">
                          {t("rp.interviewPerformance")}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                          <ScoreBar label={t("rp.relevance")} value={a.evaluation.relevance} />
                          <ScoreBar label={t("rp.supportingEvidence")} value={a.evaluation.supporting_evidence} />
                          <ScoreBar label={t("rp.organization")} value={a.evaluation.organization} />
                          <ScoreBar label={t("rp.professionalCompetency")} value={a.evaluation.professional_competency} />
                          <ScoreBar label={t("rp.evidenceOfResults")} value={a.evaluation.evidence_of_results} />
                          <ScoreBar label={t("rp.professionalPresence")} value={a.evaluation.professional_presence} />
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mt-6 mb-3">
                          {t("rp.englishLanguage")}
                        </div>
                        {a.evaluation.english_insufficient ? (
                          <div
                            className="text-sm italic text-[color:var(--text-secondary)] rounded-2xl bg-[color:var(--bg-surface-hover)] px-5 py-4 border border-dashed border-[color:var(--border-stone)]"
                            data-testid={`english-insufficient-q${i}`}
                          >
                            {t("rp.englishInsufficient")}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                            <ScoreBar label={t("rp.grammar")} value={a.evaluation.grammar} />
                            <ScoreBar label={t("rp.vocabulary")} value={a.evaluation.vocabulary} />
                            <ScoreBar label={t("rp.clarity")} value={a.evaluation.clarity} />
                            <ScoreBar label={t("rp.protone")} value={a.evaluation.professional_tone} />
                            <ScoreBar label={t("rp.fluency")} value={a.evaluation.fluency} />
                          </div>
                        )}
                      </div>

                      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">
                            {t("rp.yourTranscript")}
                          </div>
                          <div className="rounded-2xl bg-[color:var(--bg-surface-hover)] p-5 text-sm leading-relaxed">
                            {a.transcript}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">
                            {t("rp.suggestedAnswer")}
                          </div>
                          <div className="rounded-2xl bg-[color:var(--brand)]/5 border border-[color:var(--brand)]/20 p-5 text-sm leading-relaxed">
                            {a.evaluation.suggested_answer}
                          </div>
                        </div>
                      </div>

                      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="flex items-center gap-2 font-semibold text-[color:var(--brand)] mb-3">
                            <Star weight="duotone" /> {t("rp.strengths")}
                          </div>
                          <ul className="space-y-2">
                            {strengths.map((s, k) => (
                              <li key={k} className="flex gap-3 text-sm">
                                <CheckCircle
                                  size={16}
                                  weight="fill"
                                  className="mt-0.5 text-[color:var(--brand)] shrink-0"
                                />
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 font-semibold text-[color:var(--score-bad)] mb-3">
                            <Warning weight="duotone" /> {t("rp.improve")}
                          </div>
                          <ul className="space-y-2">
                            {improvements.map((s, k) => (
                              <li key={k} className="flex gap-3 text-sm">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--score-bad)] shrink-0" />
                                <span>{s}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

const COMPETENCY_KEYS = [
  "relevance",
  "supporting_evidence",
  "organization",
  "professional_competency",
  "evidence_of_results",
  "professional_presence",
  "clarity",
  "grammar",
  "vocabulary",
  "professional_tone",
  "fluency",
];

function bestArea(s, tr) {
  const items = COMPETENCY_KEYS
    .map((k) => [tr(`rp.bestFor.${k}`), s[k]])
    .filter(([, v]) => v != null);
  if (!items.length) return tr("rp.noStrengths");
  items.sort((a, b) => b[1] - a[1]);
  return items[0][0];
}

function worstArea(s, tr) {
  const items = COMPETENCY_KEYS
    .map((k) => [tr(`rp.worstFor.${k}`), s[k]])
    .filter(([, v]) => v != null);
  if (!items.length) return "";
  items.sort((a, b) => a[1] - b[1]);
  return items[0][0];
}

function DownloadsCard({ sessionId, t, tr }) {
  const [preparing, setPreparing] = useState(false);
  const [planReady, setPlanReady] = useState(false);

  const reportHref = `${api.defaults.baseURL}/sessions/${sessionId}/report.pdf`;
  const planHref = `${api.defaults.baseURL}/sessions/${sessionId}/study-plan.pdf`;

  const handleStudyGuideClick = async (e) => {
    if (planReady) return;
    e.preventDefault();
    if (preparing) return;
    setPreparing(true);
    try {
      await api.post(`/sessions/${sessionId}/study-plan`);
      setPlanReady(true);
      window.open(planHref, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("rp.planError"));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div
      data-testid="report-downloads-card"
      className="mt-6 rounded-2xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] p-5 sm:p-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-stretch">
        <div className="flex h-full flex-col items-center text-center sm:items-start sm:text-left">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-[color:var(--brand)]/15 flex items-center justify-center shrink-0">
              <ChartBar weight="duotone" size={22} className="text-[color:var(--brand)]" />
            </div>
            <div className="font-semibold tracking-tight text-base leading-snug">
              {t("rp.downloadsCardH1")}
            </div>
          </div>
          <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">
            {t("rp.downloadsCardSub1")}
          </p>
          <div className="mt-auto w-full pt-4">
            <a
              data-testid={REPORT.downloadReportBtn}
              href={reportHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-3 text-sm font-medium"
            >
              <DownloadSimple size={16} /> {t("rp.downloadsCardBtn1")}
            </a>
            <div className="mt-2 w-full text-center text-xs text-[color:var(--text-secondary)]">
              {t("rp.pdfFormat")}
            </div>
          </div>
        </div>

        <div className="flex h-full flex-col items-center text-center sm:items-start sm:text-left">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-[color:var(--brand)]/15 flex items-center justify-center shrink-0">
              <BookOpen weight="duotone" size={22} className="text-[color:var(--brand)]" />
            </div>
            <div className="font-semibold tracking-tight text-base leading-snug">
              {t("rp.downloadsCardH2")}
            </div>
          </div>
          <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">
            {t("rp.downloadsCardSub2")}
          </p>
          <div className="mt-auto w-full pt-4">
            <a
              data-testid={REPORT.downloadPlanBtn}
              href={planHref}
              onClick={handleStudyGuideClick}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex w-full items-center justify-center gap-2 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-3 text-sm font-medium ${
                preparing ? "opacity-70 cursor-wait" : ""
              }`}
              aria-busy={preparing}
            >
              <DownloadSimple size={16} />
              {preparing ? t("rp.downloadsCardBuilding") : t("rp.downloadsCardBtn2")}
            </a>
            <div className="mt-2 w-full text-center text-xs text-[color:var(--text-secondary)]">
              {t("rp.pdfFormat")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
